import { useEffect, useMemo, useState, useCallback } from "react";
import {
  Calendar, Lightbulb, RefreshCw, ChevronDown, AlertTriangle,
  Pencil, X
} from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/lib/supabaseClient";
import { recallDurationBreakdown } from "@/lib/recallDuration";
import { useBranch } from "@/context/BranchContext";
import { formatDoctorName } from "@/lib/doctorName";

// =====================================================================
// SmartScheduler — Path C, patient-led (spec §9 v2)
//
// Build Principle #8: "The patient dictates the date. The system
// narrows the slots." This component DOES NOT pre-pick slots when it
// opens. Jen sees a 💡 line asking the patient first, plus the
// Timeframe filter. Slots only appear after Jen picks a timeframe.
// =====================================================================

const TIMEFRAMES = [
  { key: "this_week",     label: "This week"    },
  { key: "next_week",     label: "Next week"    },
  { key: "in_2_weeks",    label: "In 2 weeks"   },
  { key: "this_month",    label: "This month"   },
  { key: "specific_date", label: "Specific date" },
];

// ISO day numbers (1=Mon..6=Sat). Spec specifies Sat as the last working day.
const WEEKDAYS = [
  { key: 1, label: "Mon" },
  { key: 2, label: "Tue" },
  { key: 3, label: "Wed" },
  { key: 4, label: "Thu" },
  { key: 5, label: "Fri" },
  { key: 6, label: "Sat" },
];

const TIMES_OF_DAY = [
  { key: "any",       label: "Any time" },
  { key: "morning",   label: "Morning"  },
  { key: "afternoon", label: "Afternoon"},
];

function tutorialDetailFor(row) {
  return {
    id: row.id,
    procedure_summary: "Root Canal Therapy #46",
    chair_setup_minutes: 15,
    unbooked_items: [
      {
        item_id: "tutorial-rct-46",
        procedure_name: "Root Canal Therapy",
        tooth_number: "46",
        fee_centavos: 1200000,
        duration_minutes: 60,
        uses_chair_setup: true,
      },
    ],
  };
}

const TUTORIAL_DOCTORS = [
  { id: "tutorial-doctor", display_name: "Dr. Reyes", full_name: "Dr. Reyes" },
];

const TUTORIAL_SLOTS = [
  {
    rank: 1,
    slot_start: "2026-05-29T02:00:00.000Z",
    slot_end: "2026-05-29T03:15:00.000Z",
    doctor_id: "tutorial-doctor",
    is_diagnosing_doctor: true,
  },
  {
    rank: 2,
    slot_start: "2026-05-30T05:00:00.000Z",
    slot_end: "2026-05-30T06:15:00.000Z",
    doctor_id: "tutorial-doctor",
    is_diagnosing_doctor: true,
  },
];

function fmtSlot(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString(undefined, {
    weekday: "long",
    month:   "short",
    day:     "numeric",
    hour:    "numeric",
    minute:  "2-digit",
  });
}

function fmtSlotShort(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleString(undefined, { weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
}

function inputDateValue(iso) {
  if (!iso) return "";
  const d = typeof iso === "string" ? new Date(iso) : iso;
  if (Number.isNaN(d.getTime())) return "";
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`;
}

function formatMoney(centavos, currency = "PHP") {
  const symbol = currency === "PHP" ? "₱" : currency === "USD" ? "$" : `${currency} `;
  return symbol + Math.round((centavos ?? 0) / 100).toLocaleString("en-US");
}

export default function SmartScheduler({
  row,
  channel,
  notes,
  currency = "PHP",
  tutorialDemo = false,
  onBooked,
}) {
  const { activeBranchId } = useBranch();
  // Filter state — all start NULL/empty per spec §9.1: "scheduler
  // opens with NO slots visible, only a 💡 line and timeframe filter".
  const [timeframe, setTimeframe]       = useState(null);
  const [specificDate, setSpecificDate] = useState(null);
  const [days, setDays]                 = useState([]);              // multi-select Mon-Sat
  const [timeOfDay, setTimeOfDay]       = useState("any");

  // Doctor + duration
  const [doctorMode, setDoctorMode]     = useState("default");        // 'default' | 'any' | 'specific'
  const [doctorId, setDoctorId]         = useState(row.diagnosing_doctor_id || null);
  const [doctorPickerOpen, setDoctorPickerOpen] = useState(false);
  const [doctorList, setDoctorList]     = useState([]);

  // Slot state
  const [slots, setSlots]               = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [errorSlots, setErrorSlots]     = useState(null);
  const [selected, setSelected]         = useState(null);
  const [submitting, setSubmitting]     = useState(false);

  // Detail (for duration + procedure summary)
  const [detail, setDetail] = useState(null);
  const [procedureConfirmed, setProcedureConfirmed] = useState(false);
  const [selectedItemIds, setSelectedItemIds] = useState([]);

  // -------------------------------------------------------------------
  // Load full row context once so we can show "Duration: 90 min (Crown #14)"
  // -------------------------------------------------------------------
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (tutorialDemo) {
        const demoDetail = tutorialDetailFor(row);
        setDetail(demoDetail);
        setSelectedItemIds(["tutorial-rct-46"]);
        setProcedureConfirmed(true);
        return;
      }
      const { data, error } = await supabase.rpc("get_recall_row", { p_recall_queue_id: row.id });
      if (cancelled) return;
      if (error) console.warn("[scheduler] get_recall_row:", error.message);
      setDetail(data ?? null);
      const unbookedItems = data?.unbooked_items ?? [];
      const firstItemId = unbookedItems[0]?.item_id;
      setSelectedItemIds(firstItemId ? [firstItemId] : []);
      setProcedureConfirmed(unbookedItems.length === 1);
    })();
    return () => { cancelled = true; };
  }, [row, row.id, tutorialDemo]);

  // Doctor list for the override pill dropdown
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (tutorialDemo) {
        setDoctorList(TUTORIAL_DOCTORS);
        return;
      }
      const { data, error } = await supabase.rpc("list_clinic_doctor_records", {
        p_branch_id: activeBranchId || null,
      });
      if (cancelled) return;
      if (error) console.warn("[scheduler] list_clinic_doctor_records:", error.message);
      setDoctorList(data ?? []);
    })();
    return () => { cancelled = true; };
  }, [activeBranchId, tutorialDemo]);

  // -------------------------------------------------------------------
  // Slot fetch — only when timeframe is set. The backend won't return
  // slots without timeframe anyway, but we also short-circuit here so
  // we don't show a loading state for nothing.
  // -------------------------------------------------------------------
  const loadSlots = useCallback(async () => {
    if (!timeframe || !procedureConfirmed || selectedItemIds.length === 0) return;
    setLoadingSlots(true);
    setErrorSlots(null);
    setSelected(null);

    if (tutorialDemo) {
      setSlots(TUTORIAL_SLOTS);
      setLoadingSlots(false);
      return;
    }

    const params = {
      p_recall_queue_id: row.id,
      p_timeframe:       timeframe,
      p_specific_date:   timeframe === "specific_date" ? specificDate : null,
      p_day_of_week:     days.length > 0 ? days : null,
      p_time_of_day:     timeOfDay || "any",
      p_doctor_id:       doctorMode === "specific" ? doctorId
                       : doctorMode === "default"  ? (row.diagnosing_doctor_id || null)
                       : null,
      p_any_doctor:      doctorMode === "any",
      p_item_ids:        selectedItemIds,
      p_branch_id:       activeBranchId || null,
    };
    const { data, error } = await supabase.rpc("suggest_recall_slots", params);
    setLoadingSlots(false);
    if (error) {
      setErrorSlots(error.message ?? "Couldn't load slot suggestions.");
      setSlots([]);
      return;
    }
    setSlots(data ?? []);
  }, [activeBranchId, row.id, row.diagnosing_doctor_id, timeframe, specificDate, days, timeOfDay, doctorMode, doctorId, procedureConfirmed, selectedItemIds, tutorialDemo]);

  useEffect(() => { loadSlots(); }, [loadSlots]);

  // -------------------------------------------------------------------
  // Helpers
  // -------------------------------------------------------------------
  function toggleDay(k) {
    setDays((prev) => prev.includes(k) ? prev.filter((x) => x !== k) : [...prev, k]);
  }

  function toggleItem(itemId) {
    setSelectedItemIds((prev) => {
      const next = prev.includes(itemId) ? prev.filter((id) => id !== itemId) : [...prev, itemId];
      setSelected(null);
      setSlots([]);
      return next;
    });
  }

  function editProcedures() {
    setProcedureConfirmed(false);
    setTimeframe(null);
    setSpecificDate(null);
    setDays([]);
    setSelected(null);
    setSlots([]);
  }

  function nextMoveTryAfternoon() { setTimeOfDay("afternoon"); }
  function nextMoveTryAnotherDay() { setDays([]); setTimeOfDay("any"); }
  function nextMoveAnyDoctor() { setDoctorMode("any"); }

  async function confirm() {
    if (submitting) return;
    if (!selected) { toast.error("Pick a slot first."); return; }

    // Pick a doctor: when doctorMode is 'any', the backend may have
    // returned a NULL doctor_id; we still need to attach SOMEONE to
    // the appointment. Force the diagnosing doctor as a fallback if
    // the row provides one; otherwise the booking RPC will reject.
    const slotMatch = slots.find((s) => s.slot_start === selected);
    const doctorIdForBooking =
      doctorMode === "specific" ? doctorId
      : (slotMatch?.doctor_id || row.diagnosing_doctor_id || null);

    if (!doctorIdForBooking) {
      toast.error("Pick a specific doctor before confirming.");
      return;
    }

    setSubmitting(true);
    const duration = slotMatch
      ? Math.round((new Date(slotMatch.slot_end).getTime() - new Date(slotMatch.slot_start).getTime()) / 60000)
      : selectedDurationMinutes;

    if (tutorialDemo) {
      setSubmitting(false);
      toast.success("Tutorial booking confirmed locally.");
      onBooked?.({ tutorial: true, items_booked: selectedItemIds.length, items_remaining: 0 });
      return;
    }

    const { data, error } = await supabase.rpc("book_recall_appointment", {
      p_recall_queue_id:  row.id,
      p_slot_start:       selected,
      p_duration_minutes: duration,
      p_doctor_id:        doctorIdForBooking,
      p_chair_id:         null,
      p_item_ids:         selectedItemIds,
      p_channel:          channel === "messenger" ? "messenger" : "call",
      p_notes:            notes || null,
      p_branch_id:        activeBranchId || null,
    });
    setSubmitting(false);
    if (error) {
      toast.error(error.message ?? "Couldn't book the appointment.");
      return;
    }
    const items = data?.items_booked ?? 0;
    const remaining = data?.items_remaining ?? 0;
    if (remaining > 0) {
      toast.success(`Booked ${items} — ${remaining} still pending in recall.`);
    } else {
      toast.success("Booked! Patient exits recall.");
    }
    onBooked?.(data);
  }

  // -------------------------------------------------------------------
  // Derived state for rendering
  // -------------------------------------------------------------------
  const showFiltersAndSlots = !!timeframe;
  const showSpecificDateInput = timeframe === "specific_date";
  const items = useMemo(() => detail?.unbooked_items ?? [], [detail?.unbooked_items]);
  const selectedItems = useMemo(
    () => items.filter((item) => selectedItemIds.includes(item.item_id)),
    [items, selectedItemIds]
  );
  const selectedDurationBreakdown = useMemo(
    () => recallDurationBreakdown(selectedItems, detail?.chair_setup_minutes ?? 15),
    [selectedItems, detail?.chair_setup_minutes],
  );
  const selectedDurationMinutes = Math.max(
    30,
    selectedDurationBreakdown.recommendedMinutes || 60
  );
  const selectedProcedureSummary = selectedItems.length > 0
    ? selectedItems.map((item) => item.procedure_name).join(" + ")
    : "";

  // Doctor pill label
  const diagnosingDoctorName = formatDoctorName(row.diagnosing_doctor_name, "Unassigned");
  const doctorPillLabel =
    doctorMode === "any" ? "Any doctor"
    : doctorMode === "specific"
      ? formatDoctorName(doctorList.find((d) => d.id === doctorId), "Pick a doctor")
      : (row.diagnosing_doctor_id ? diagnosingDoctorName : "Pick a doctor");

  // Empty-state branch
  const isEmpty = showFiltersAndSlots && !loadingSlots && !errorSlots && slots.length === 0;
  const filterSummary = (() => {
    const parts = [];
    if (timeframe === "specific_date" && specificDate) {
      parts.push(new Date(specificDate).toLocaleDateString(undefined, { weekday: "long", month: "short", day: "numeric" }));
    } else if (timeframe) {
      parts.push(TIMEFRAMES.find((t) => t.key === timeframe)?.label.toLowerCase());
    }
    if (timeOfDay && timeOfDay !== "any") parts.push(timeOfDay);
    if (days.length > 0) parts.push(days.map((d) => WEEKDAYS.find((w) => w.key === d)?.label).join("/"));
    return parts.join(" · ");
  })();

  return (
    <div className="mt-3 space-y-3 bg-white border border-slate-200 rounded-lg p-3" data-testid="recall-scheduler" data-tutorial-id="recall-scheduler">
      {/* Header — doctor pill + duration info */}
      <div className="flex items-start justify-between gap-2">
        <div>
          <h4 className="text-[13px] font-semibold text-slate-900 inline-flex items-center gap-1.5">
            <Calendar className="w-4 h-4 text-teal-600" /> Find a slot
          </h4>
          <div className="mt-1 flex flex-wrap items-center gap-2 text-[11px] text-slate-600">
            {/* Doctor pill */}
            <DoctorPill
              label={doctorPillLabel}
              isDefault={doctorMode === "default" && !!row.diagnosing_doctor_id}
              open={doctorPickerOpen}
              onToggle={() => setDoctorPickerOpen((v) => !v)}
            />
            {doctorPickerOpen ? (
              <DoctorOverrideMenu
                doctorList={doctorList}
                diagnosing={row.diagnosing_doctor_id}
                doctorMode={doctorMode}
                doctorId={doctorId}
                onPick={(mode, id) => {
                  setDoctorMode(mode);
                  setDoctorId(id ?? null);
                  setDoctorPickerOpen(false);
                }}
                onClose={() => setDoctorPickerOpen(false)}
              />
            ) : null}

            <span className="text-slate-300">·</span>
            <DurationInfo
              minutes={selectedDurationMinutes}
              summary={selectedProcedureSummary || detail?.procedure_summary}
              breakdown={selectedDurationBreakdown}
            />
          </div>
        </div>
        {showFiltersAndSlots ? (
          <button
            type="button"
            onClick={loadSlots}
            disabled={loadingSlots}
            className="inline-flex items-center gap-1 text-[11px] font-medium text-slate-500 hover:text-slate-800 disabled:opacity-50"
            title="Reload"
          >
            <RefreshCw className={`w-3 h-3 ${loadingSlots ? "animate-spin" : ""}`} /> Reload
          </button>
        ) : null}
      </div>

      {!procedureConfirmed && items.length > 1 ? (
        <ProcedureChecklist
          items={items}
          selectedItemIds={selectedItemIds}
          currency={currency}
          loading={!detail}
          onToggle={toggleItem}
          onContinue={() => setProcedureConfirmed(true)}
        />
      ) : procedureConfirmed ? (
        <div className="rounded-lg border border-teal-200 bg-teal-50 px-3 py-2 text-[12px] text-teal-900" data-testid="recall-procedure-summary">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <span><strong>{selectedItems.length}</strong> procedure{selectedItems.length === 1 ? "" : "s"} selected for this visit, {selectedDurationMinutes} min recommended.</span>
            <button type="button" onClick={editProcedures} className="font-semibold text-teal-700 hover:text-teal-900">
              Edit
            </button>
          </div>
        </div>
      ) : null}

      {/* Initial 💡 line — replaced after timeframe is chosen */}
      {procedureConfirmed ? <SayLine line={
        !timeframe
          ? `"What day works best for you?"`
          : timeframe === "specific_date" && !specificDate
            ? `"Which date were you thinking?"`
            : !showFiltersAndSlots
              ? ""
              : selected
                ? `"How about ${fmtSlot(selected)}?"`
                : days.length === 0 && timeOfDay === "any"
                  ? `"Any specific day, or morning vs afternoon?"`
                  : isEmpty
                    ? `"Dr. ${diagnosingDoctorName} is fully booked then. Would another time work?"`
                    : `"I have these slots — which works?"`
      } /> : null}

      {/* Filter 1 — Timeframe (always visible, REQUIRED) */}
      {procedureConfirmed ? <div>
        <div className="text-[11px] uppercase tracking-wider font-semibold text-slate-500 mb-1.5">
          When does {firstNameOf(row)} want to come?
        </div>
        <div className="flex flex-wrap gap-1.5">
          {TIMEFRAMES.map((t) => (
            <button
              key={t.key}
              type="button"
              onClick={() => {
                setTimeframe(t.key);
                if (t.key !== "specific_date") setSpecificDate(null);
              }}
              data-testid={`recall-timeframe-${t.key}`}
              data-tutorial-id={`recall-timeframe-${t.key.replaceAll("_", "-")}`}
              className={`px-3 py-1.5 text-[12px] font-medium rounded-lg border transition-colors ${
                timeframe === t.key
                  ? "bg-teal-50 text-teal-700 border-teal-200 ring-2 ring-teal-200"
                  : "bg-white text-slate-700 border-slate-200 hover:bg-slate-50"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* Specific date input — only when chosen */}
        {showSpecificDateInput ? (
          <div className="mt-2 flex items-center gap-2">
            <input
              type="date"
              value={inputDateValue(specificDate)}
              onChange={(e) => setSpecificDate(e.target.value || null)}
              min={inputDateValue(new Date().toISOString())}
              className="px-3 py-1.5 text-sm rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-teal-500/40 focus:border-teal-500"
              data-testid="recall-specific-date-input"
            />
            <span className="text-[11px] text-slate-500">Pick a day to see open slots.</span>
          </div>
        ) : null}
      </div> : null}

      {/* Filters 2 + 3 — only after timeframe is set, and never when
          specific_date (we already know the day) */}
      {showFiltersAndSlots && timeframe !== "specific_date" ? (
        <>
          <div>
            <div className="text-[11px] uppercase tracking-wider font-semibold text-slate-500 mb-1.5">
              Day preference (optional)
            </div>
            <div className="flex flex-wrap gap-1.5">
              {WEEKDAYS.map((d) => {
                const active = days.includes(d.key);
                return (
                  <button
                    key={d.key}
                    type="button"
                    onClick={() => toggleDay(d.key)}
                    data-testid={`recall-day-${d.key}`}
                    className={`px-2.5 py-1 text-[12px] font-medium rounded-md border transition-colors ${
                      active
                        ? "bg-teal-50 text-teal-700 border-teal-200 ring-1 ring-teal-300"
                        : "bg-white text-slate-700 border-slate-200 hover:bg-slate-50"
                    }`}
                  >
                    {d.label}
                  </button>
                );
              })}
              <button
                type="button"
                onClick={() => setDays([])}
                className={`px-2.5 py-1 text-[12px] font-medium rounded-md border transition-colors ${
                  days.length === 0
                    ? "bg-slate-100 text-slate-700 border-slate-200 ring-1 ring-slate-300"
                    : "bg-white text-slate-500 border-slate-200 hover:bg-slate-50"
                }`}
                data-testid="recall-day-any"
              >
                Any day
              </button>
            </div>
          </div>

          <div>
            <div className="text-[11px] uppercase tracking-wider font-semibold text-slate-500 mb-1.5">
              Time preference (optional)
            </div>
            <div className="flex flex-wrap gap-1.5">
              {TIMES_OF_DAY.map((t) => (
                <button
                  key={t.key}
                  type="button"
                  onClick={() => setTimeOfDay(t.key)}
                  data-testid={`recall-time-${t.key}`}
                  className={`px-2.5 py-1 text-[12px] font-medium rounded-md border transition-colors ${
                    timeOfDay === t.key
                      ? "bg-teal-50 text-teal-700 border-teal-200 ring-1 ring-teal-300"
                      : "bg-white text-slate-700 border-slate-200 hover:bg-slate-50"
                  }`}
                >
                  {t.label}
                </button>
              ))}
            </div>
          </div>
        </>
      ) : null}

      {/* Slot list — only after timeframe is set (and a date for specific_date) */}
      {showFiltersAndSlots && (timeframe !== "specific_date" || specificDate) ? (
        errorSlots ? (
          <div className="bg-rose-50 border border-rose-200 rounded-lg p-2 text-[12px] text-rose-700">
            {errorSlots}
          </div>
        ) : loadingSlots ? (
          <div className="text-[12px] text-slate-400">Looking for free slots…</div>
        ) : isEmpty ? (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 space-y-2" data-testid="recall-scheduler-empty">
            <div className="text-[12px] text-amber-900 inline-flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
              <div>
                No matches for {filterSummary || "those filters"}{row.diagnosing_doctor_id && doctorMode !== "any" ? ` with Dr. ${diagnosingDoctorName}` : ""}.
              </div>
            </div>
            <div className="flex flex-wrap gap-1.5 pt-1">
              {timeOfDay !== "afternoon" ? (
                <button
                  type="button"
                  onClick={nextMoveTryAfternoon}
                  className="px-2.5 py-1 text-[12px] font-medium rounded-md border border-amber-300 text-amber-900 bg-white hover:bg-amber-100"
                  data-testid="recall-empty-try-afternoon"
                >
                  Try afternoon
                </button>
              ) : null}
              {(days.length > 0 || timeOfDay !== "any") ? (
                <button
                  type="button"
                  onClick={nextMoveTryAnotherDay}
                  className="px-2.5 py-1 text-[12px] font-medium rounded-md border border-amber-300 text-amber-900 bg-white hover:bg-amber-100"
                  data-testid="recall-empty-try-day"
                >
                  Try another day
                </button>
              ) : null}
              {row.diagnosing_doctor_id && doctorMode !== "any" ? (
                <button
                  type="button"
                  onClick={nextMoveAnyDoctor}
                  className="px-2.5 py-1 text-[12px] font-medium rounded-md border border-amber-300 text-amber-900 bg-white hover:bg-amber-100"
                  data-testid="recall-empty-any-doctor"
                >
                  Try another doctor
                </button>
              ) : null}
            </div>
          </div>
        ) : (
          <ul className="space-y-1.5" data-testid="recall-scheduler-slots" data-tutorial-id="recall-scheduler-slots">
            {slots.map((s) => {
              const active = selected === s.slot_start;
              return (
                <li key={s.slot_start}>
                  <button
                    type="button"
                    onClick={() => setSelected(s.slot_start)}
                    data-testid={`recall-slot-${s.rank}`}
                    data-tutorial-id={`recall-slot-${s.rank}`}
                    className={`w-full text-left px-3 py-2 rounded-lg border transition-colors flex items-center gap-3 ${
                      active
                        ? "bg-teal-50 border-teal-300 ring-2 ring-teal-200"
                        : "bg-white border-slate-200 hover:border-teal-200 hover:bg-slate-50"
                    }`}
                  >
                    <span className={`inline-flex items-center justify-center w-5 h-5 rounded-full text-[10px] font-bold ${
                      active ? "bg-teal-600 text-white" : "bg-slate-100 text-slate-500"
                    }`}>
                      {s.rank}
                    </span>
                    <span className="flex-1 text-sm font-medium text-slate-800">
                      {fmtSlot(s.slot_start)}
                    </span>
                    {s.is_diagnosing_doctor ? (
                      <span className="text-[10px] uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded-full bg-violet-50 text-violet-700 border border-violet-200">
                        Same doctor
                      </span>
                    ) : null}
                  </button>
                </li>
              );
            })}
          </ul>
        )
      ) : null}

      {/* Confirm button */}
      <div className="flex items-center justify-end gap-2 pt-1">
        <button
          type="button"
          disabled={submitting || !selected}
          onClick={confirm}
          data-testid="recall-scheduler-confirm"
          data-tutorial-id="recall-scheduler-confirm"
          className="px-4 py-2 text-sm font-semibold rounded-lg bg-teal-600 text-white hover:bg-teal-700 transition-colors shadow-sm disabled:bg-slate-300 disabled:cursor-not-allowed"
        >
          {submitting
            ? "Booking…"
            : selected
              ? `Confirm Booking · ${fmtSlotShort(selected)}`
              : "— Pick a slot —"}
        </button>
      </div>
    </div>
  );
}

// =====================================================================
// Helpers / sub-components
// =====================================================================

function firstNameOf(row) {
  const name = (row.patient_name || "").trim();
  return name.split(/\s+/)[0] || "the patient";
}

function ProcedureChecklist({ items, selectedItemIds, currency, loading, onToggle, onContinue }) {
  return (
    <div className="rounded-lg border border-slate-200 bg-slate-50 p-3" data-testid="recall-procedure-checklist">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h5 className="text-[13px] font-semibold text-slate-900">Confirm procedures for this visit</h5>
          <p className="mt-0.5 text-[12px] text-slate-500">Choose only the work the patient is agreeing to book now.</p>
        </div>
        <button
          type="button"
          onClick={onContinue}
          disabled={loading || selectedItemIds.length === 0}
          className="rounded-md bg-teal-600 px-3 py-1.5 text-[12px] font-semibold text-white hover:bg-teal-700 disabled:bg-slate-300 disabled:cursor-not-allowed"
          data-testid="recall-procedure-continue"
        >
          Find slots
        </button>
      </div>
      {loading ? (
        <p className="mt-3 text-[12px] text-slate-400">Loading items...</p>
      ) : items.length === 0 ? (
        <p className="mt-3 text-[12px] text-slate-400">No pending procedure snapshot available.</p>
      ) : (
        <div className="mt-3 space-y-2">
          {items.map((item) => {
            const checked = selectedItemIds.includes(item.item_id);
            return (
              <label
                key={item.item_id}
                className={[
                  "flex cursor-pointer items-start gap-3 rounded-md border px-3 py-2",
                  checked ? "border-teal-300 bg-white ring-2 ring-teal-100" : "border-slate-200 bg-white hover:border-teal-200",
                ].join(" ")}
              >
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={() => onToggle(item.item_id)}
                  className="mt-1 h-4 w-4 rounded border-slate-300 text-teal-600 focus:ring-teal-500"
                  data-testid={`recall-procedure-check-${item.item_id}`}
                />
                <span className="min-w-0 flex-1">
                  <span className="block text-[13px] font-semibold text-slate-900">{item.procedure_name}</span>
                  <span className="mt-0.5 block text-[11px] text-slate-500">
                    {[item.tooth_number ? `Tooth ${item.tooth_number}` : null, `${item.duration_minutes || 60} min`].filter(Boolean).join(" · ")}
                  </span>
                </span>
                <span className="text-[12px] font-semibold tabular-nums text-slate-700">{formatMoney(item.fee_centavos, currency)}</span>
              </label>
            );
          })}
        </div>
      )}
    </div>
  );
}

function SayLine({ line }) {
  if (!line) return null;
  return (
    <div className="bg-amber-50/60 border border-amber-200 rounded-lg p-2 text-[12px] text-amber-900 inline-flex items-start gap-2 w-full">
      <Lightbulb className="w-4 h-4 shrink-0 mt-0.5 text-amber-600" />
      <div className="font-medium">💡 Say: {line}</div>
    </div>
  );
}

function DoctorPill({ label, isDefault, open, onToggle }) {
  return (
    <button
      type="button"
      onClick={onToggle}
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[11px] font-medium ${
        isDefault
          ? "bg-violet-50 text-violet-700 border-violet-200 hover:bg-violet-100"
          : "bg-amber-50 text-amber-800 border-amber-200 hover:bg-amber-100"
      }`}
      data-testid="recall-doctor-pill"
    >
      {label} {isDefault ? "✓" : ""}
      <ChevronDown className={`w-3 h-3 transition-transform ${open ? "rotate-180" : ""}`} />
    </button>
  );
}

function DoctorOverrideMenu({ doctorList, diagnosing, doctorMode, doctorId, onPick, onClose }) {
  return (
    <div className="bg-white border border-slate-200 rounded-lg shadow-md p-2 w-56 absolute z-10 mt-7" data-testid="recall-doctor-menu">
      <div className="flex items-center justify-between mb-1">
        <span className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">Doctor</span>
        <button type="button" onClick={onClose} className="text-slate-400 hover:text-slate-700"><X className="w-3 h-3" /></button>
      </div>
      <ul className="space-y-0.5">
        <li>
          <button
            type="button"
            onClick={() => onPick("default", diagnosing)}
            disabled={!diagnosing}
            className={`w-full text-left px-2 py-1 text-[12px] rounded ${
              doctorMode === "default" ? "bg-violet-50 text-violet-700 font-semibold" : "text-slate-700 hover:bg-slate-50"
            } disabled:text-slate-300`}
          >
            Diagnosing doctor {diagnosing ? "(default)" : "(none)"}
          </button>
        </li>
        <li>
          <button
            type="button"
            onClick={() => onPick("any", null)}
            className={`w-full text-left px-2 py-1 text-[12px] rounded ${
              doctorMode === "any" ? "bg-amber-50 text-amber-800 font-semibold" : "text-slate-700 hover:bg-slate-50"
            }`}
            data-testid="recall-doctor-any"
          >
            Any doctor
          </button>
        </li>
        <li className="border-t border-slate-100 my-1" />
        {doctorList.map((d) => (
          <li key={d.id}>
            <button
              type="button"
              onClick={() => onPick("specific", d.id)}
              className={`w-full text-left px-2 py-1 text-[12px] rounded truncate ${
                doctorMode === "specific" && doctorId === d.id
                  ? "bg-teal-50 text-teal-700 font-semibold"
                  : "text-slate-700 hover:bg-slate-50"
              }`}
            >
              {d.display_name}
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

function DurationInfo({ minutes, summary, breakdown }) {
  if (!minutes) {
    return <span className="text-slate-400">Computing duration…</span>;
  }
  const raw = Number(breakdown?.rawProcedureMinutes) || 0;
  const setup = Number(breakdown?.chairSetupMinutes) || 0;
  return (
    <span className="inline-flex items-center gap-1">
      <span>Recommended duration: <strong>{minutes} min</strong></span>
      {summary ? <span className="text-slate-500">({summary})</span> : null}
      {raw > 0 ? (
        <span className="text-emerald-700">
          {raw} min procedure{setup > 0 ? ` + ${setup} min setup` : ""}
        </span>
      ) : null}
      <button
        type="button"
        onClick={() => { /* override hook — placeholder for v1.1 */ }}
        className="text-slate-300 hover:text-slate-500 ml-0.5"
        title="Duration is auto-calculated from procedures (override coming later)"
      >
        <Pencil className="w-3 h-3" />
      </button>
    </span>
  );
}
